import { setPointIsHome } from "./place-word";
import "maplibre-gl/dist/maplibre-gl.css";
import "./styles.css";
import { startPageTheme, toggleTheme } from "./theme";
import { createMap, addRiversLayer, setRiversVisible, setRiverColorMode, addRiverLabelsLayer } from "./map";
import { loadRiverNames } from "./river-names";
import { addRouteLayer, setRoute, setRadius, clearRoute, setStartLabel, setRadiusMask, clearRadiusMask, type FlowStatus } from "./route-layer";
import { setRainVisible } from "./rain";
import { addGistdaLayers, setGistdaWindow, setGistdaAllZoom, setGistdaRadiusActive, setGistdaOverview } from "./gistda-layer";
import { addNasaFloodLayers, setNasaFloodWindow } from "./nasa-flood-layer";
import { addSarFloodLayer, setSarFloodVisible } from "./sar-flood-layer";
import { setBkkGaugesVisible, floodGauges, setGaugeFocus } from "./bkk-layer";
import { setThaiWaterVisible, loadThaiWaterData, initThaiWaterLayer } from "./thaiwater-layer";
import { startAmbient, setAmbientEnabled } from "./ambient-flow";
import { installDamZoneLayers, showDamZones, clearDamZones, damZonesActive, restoreDamView } from "./dam-rain";
import { loadStorms, renderStorms, showStormOverlay, hideStormOverlay, stormsLoaded, openGlobalStorms } from "./storms";
import { setDamsVisible, setDamsY54 } from "./dam-layer";
import { mountUI, DEFAULT_FLOOD_WINDOW } from "./ui";
import { createFlowAnim, type FlowAnim } from "./flow-anim";
import { loadSimData, isSimReady, analyze } from "./sim-client";
import { renderSummary, renderMessage, clearResult } from "./result";
import { parseShareUrl } from "./share";
import { mountSearch, areaCenterLabel, type SearchEntry } from "./search";
import { renderTopFlood } from "./top-flood";
import { renderCameraPanel, clearCameraMarkers } from "./cameras";
import { markerTapRecent } from "./popup-room";
import { openSummary } from "./summary-ui";
import { mountRiverChip } from "./river-chip";
import { openPastStormMenu } from "./past-storm";
import { mountSosButton } from "./sos";
import { mountTopRight, type TopRightHandles } from "./top-right";
import { toggleMonsoonPanel } from "./monsoon";
import { installProvinceRain, suspendProvinceRain, provinceRainActive } from "./province-rain";
import { SUMMARY_RULES } from "@flowsight/sim";
import { clearSummary } from "./summary-store";
import { initPwa } from "./pwa";

/**
 * FlowSight P3 — "see water near me". One button → GPS (or tap map) → snap to the
 * nearest river → show radius, route to the outlet, ETA range + live flow status.
 */

const mapEl = document.getElementById("map");
const uiEl = document.getElementById("ui");
if (!mapEl || !uiEl) throw new Error("missing #map / #ui");

let mapRef: ReturnType<typeof createMap> | null = null;
const withMap = (fn: (m: NonNullable<typeof mapRef>) => void): void => { if (mapRef) fn(mapRef); };

let radiusKm = 3;
let active: { lat: number; lon: number } | null = null;

const ui = mountUI(uiEl, {
  onLocate: () => locate(),
  onToggleRivers: (on) => withMap((m) => setRiversVisible(m, on)),
  onToggleRain: (on) => withMap((m) => setRainVisible(m, on)),
  onColorMode: (mode) => withMap((m) => setRiverColorMode(m, mode)),
  onFloodLayer: (w) => withMap((m) => setGistdaWindow(m, w)),
  onNasaFloodLayer: (w) => withMap((m) => setNasaFloodWindow(m, w)),
  onToggleSar: (on) => withMap((m) => setSarFloodVisible(m, on)),
  onToggleGauges: (on) => withMap((m) => setBkkGaugesVisible(m, on)),
  onToggleThaiWater: (on) => withMap((m) => setThaiWaterVisible(m, on)),
  onToggleAmbient: (on) => setAmbientEnabled(on),
  onToggleDams: (on) => withMap((m) => setDamsVisible(m, on)),
  onToggleY54: (on) => withMap((m) => void setDamsY54(m, on)),
  onPastStorms: () => withMap((m) => void openPastStormMenu(m)),
  onTopFlood: () => openTopFlood(),
  onGistdaAllZoom: (on) => withMap((m) => setGistdaAllZoom(m, on)),
  onRadius: (km) => { radiusKm = km; if (active) liveRadius(active, km); },
  onRadiusCommit: (km) => { radiusKm = km; debounceRecompute(); },
  onFlow: () => withMap((m) => {
    if (!flowAnim) { flowAnim = createFlowAnim(m, () => active); (window as unknown as { __flow?: unknown }).__flow = flowAnim; } // e2e handle
    void flowAnim.open();
  }),
  onSummary: () => openSummary(),
});
let flowAnim: FlowAnim | null = null;

mountSearch(goToSearch);

// PWA: register the service worker + offer install / surface updates (no-op for automated gates)
initPwa();

// reshow the current water card (used by "✕ ปิด" on the Top-flood panel)
let reshow: (() => void) | null = null;

/* ── v0.9.9 A: "🇹🇭 ทั้งประเทศ" ⇄ "📍 รอบตัวฉัน" ─────────────────────────────── */
const TH_BOUNDS: [[number, number], [number, number]] = [[97.3, 5.6], [105.7, 20.5]];
// the point's own overlays: hidden in the country view, shown again (as they were) on the way back
const POINT_LAYERS = ["radius-fill", "radius-line", "radius-mask-fill", "route-glow", "route-highlight", "route-names", "dr-zones-fill", "dr-div-line", "dr-div-dot", "dr-user-dot", "dr-user-label"];
let topRight: TopRightHandles | null = null;
let overview = false;
let overviewSaved = new Map<string, string>();
let overviewSheetOpen = false; // phones: the sheet folds for the country view and unfolds on the way back
function updateCountryBtn(): void {
  if (!topRight || !mapRef) return;
  if (overview && active) topRight.setCountry("back");
  // "zoomed in beyond the country" = closer than the zoom that fits all of Thailand on this screen
  else if (active || mapRef.getZoom() > (mapRef.cameraForBounds(TH_BOUNDS, { padding: sheetPad() })?.zoom ?? 5.5) + 0.3) topRight.setCountry("country");
  else topRight.setCountry("hidden");
}
/** Country view: all of Thailand + the first-visit layers (province rain bar, dams, staffs, flow);
 *  the 📍 stays, the point is kept (nothing re-fetched). */
function enterOverview(): void {
  overview = true;
  withMap((m) => {
    overviewSaved = new Map();
    for (const id of POINT_LAYERS) if (m.getLayer(id)) { overviewSaved.set(id, String(m.getLayoutProperty(id, "visibility") ?? "visible")); m.setLayoutProperty(id, "visibility", "none"); }
    setGistdaRadiusActive(m, false);
    setGistdaOverview(m, true);
    void setGaugeFocus(m, null, 0);
  });
  suspendProvinceRain(false);
  document.body.classList.add("overview");
  const sh = document.querySelector(".sheet");
  overviewSheetOpen = !!sh && !sh.classList.contains("collapsed");
  window.dispatchEvent(new Event("fs-sheet-collapse"));
  fitThailand();
  updateCountryBtn();
}
/** Back to the point exactly as it was: same view, radius and card — no new location, no new API call. */
function exitOverview(fit = true): void {
  if (!overview) return;
  overview = false;
  withMap((m) => {
    setGistdaOverview(m, false);
    for (const [id, v] of overviewSaved) if (m.getLayer(id)) m.setLayoutProperty(id, "visibility", v as "visible" | "none");
    if (active) { setGistdaRadiusActive(m, true); void setGaugeFocus(m, active, radiusKm); }
  });
  suspendProvinceRain(active != null);
  document.body.classList.remove("overview");
  if (fit && overviewSheetOpen) window.dispatchEvent(new Event("fs-sheet-uncollapse"));
  if (fit && active) fitRadius(active, radiusKm, 600);
  updateCountryBtn();
}
function toggleCountry(): void { if (overview && active) exitOverview(); else enterOverview(); }
/** Fit Thailand into the map area that is really free (under the top bars, above the bottom
 *  buttons / collapsed sheet, left of the top-right group) — measured after the sheet folds. */
function fitThailand(): void {
  window.setTimeout(() => withMap((m) => {
    const W = window.innerWidth, H = window.innerHeight;
    const vis = (q: string): DOMRect | null => { const e = document.querySelector<HTMLElement>(q); if (!e || e.hidden || getComputedStyle(e).display === "none") return null; const r = e.getBoundingClientRect(); return r.width > 0 ? r : null; };
    let top = 12, bottom = H - 12;
    for (const q of [".search-box", ".pr-days"]) { const r = vis(q); if (r) top = Math.max(top, r.bottom + 8); }
    for (const q of ["#locate-fab", "#summary-fab", "#flow-fab", "#sos-fab", ".sheet"]) { const r = vis(q); if (r && r.top > H / 2) bottom = Math.min(bottom, r.top - 8); }
    const stack = vis(".tr-stack"), sheet = vis(".sheet");
    const right = W >= 820 ? (sheet && sheet.left > W / 2 ? W - sheet.left + 12 : 12) + (stack ? 64 : 0) : stack ? W - stack.left + 8 : 12;
    const pad = { top, bottom: Math.max(12, H - bottom), left: 12, right };
    if (H - pad.top - pad.bottom < 120 || W - pad.left - pad.right < 120) { pad.top = 12; pad.bottom = 12; } // never an impossible fit
    m.fitBounds(TH_BOUNDS, { padding: pad, duration: 600 });
  }), 350);
}

function openTopFlood(): void {
  ui.setLocateVisible(false); // hide the FABs while the panel is open
  void renderTopFlood(ui.resultHost, {
    center: active,
    radiusKm,
    onPick: (g) => {
      withMap((m) => m.flyTo({ center: [g.lon, g.lat], zoom: 14, duration: 600 }));
      analyzeAt(g.lon, g.lat, { centerLabel: `📍 จุดวัด ${g.name}`, back: openTopFlood });
    },
    onPickStation: (lat, lon, label) => {
      withMap((m) => m.flyTo({ center: [lon, lat], zoom: 13, duration: 600 }));
      analyzeAt(lon, lat, { centerLabel: label, back: openTopFlood });
    },
    onClose: () => { if (reshow) reshow(); else { clearResult(ui.resultHost); withMap((m) => clearRoute(m)); ui.setLocateVisible(true); } },
  });
  ui.expand();
}

function openCameras(): void {
  ui.setLocateVisible(false);
  if (!mapRef) return;
  void renderCameraPanel(ui.resultHost, {
    map: mapRef,
    center: active,
    radiusKm,
    onExpandRadius: (km) => { radiusKm = km; if (active) withMap((m) => setRadius(m, active!, km)); openCameras(); },
    onClose: () => { clearCameraMarkers(); if (reshow) reshow(); else { clearResult(ui.resultHost); withMap((m) => clearRoute(m)); ui.setLocateVisible(true); } },
  });
  ui.expand();
}

/* ── v0.9.13 "🌀 พายุรอบโลก": live global cyclones affecting Thailand (spin + name); the historical
 * track simulation is nested one level deeper, as a toggle inside the panel ─────────────── */
let pastStormsAvailable = false; // flipped on once /data/storms-past is deployed (HEAD check below)
function openWorldStorms(): void {
  withMap((m) => openGlobalStorms(m, { onSimulate: () => void openPastStormMenu(m), simAvailable: pastStormsAvailable }));
}

/* ── v0.9.12 "🌦 ดูสภาพอากาศ": draw / hide the live-storm overlay on demand ─────────── */
let weatherOn = false;
async function toggleWeather(): Promise<void> {
  weatherOn = !weatherOn;
  topRight?.setWeather(weatherOn);
  if (!weatherOn) { withMap((m) => hideStormOverlay(m)); return; }
  // fetch only if the start-up load hasn't landed yet (it usually has by now)
  const data = stormsLoaded() ?? await loadStorms();
  withMap((m) => { if (data && data.storms.length) showStormOverlay(m); });
  if (!data || !data.storms.length) weatherToast("ขณะนี้ไม่พบพายุหมุนเขตร้อนใกล้ไทย");
}
// tapping the "🌀 พายุ" row in the summary reveals the overlay (only turns it on, never off)
window.addEventListener("fs-show-weather", () => { if (!weatherOn) void toggleWeather(); });

/* ── v0.9.14 "🌬 ลมมรสุม": open/close the monsoon & cold-air panel (animation + real wind/ENSO) ── */
let monsoonOn = false;
async function toggleMonsoon(): Promise<void> {
  monsoonOn = await toggleMonsoonPanel();
  topRight?.setMonsoon(monsoonOn);
}
// the panel closes itself with its ✕; keep the button's pressed state in sync if that happens
window.addEventListener("fs-monsoon-closed", () => { monsoonOn = false; topRight?.setMonsoon(false); });
/** A short self-dismissing line — used when "ดูสภาพอากาศ" is tapped but no storm is near. */
let toastTimer = 0;
function weatherToast(msg: string): void {
  document.querySelector(".weather-toast")?.remove();
  const t = document.createElement("div");
  t.className = "weather-toast"; t.setAttribute("role", "status"); t.setAttribute("aria-live", "polite");
  t.textContent = `🌦 ${msg}`;
  document.body.append(t);
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => t.remove(), 3200);
}

function analyzeAt(lon: number, lat: number, opts?: { centerLabel?: string | undefined; back?: (() => void) | undefined; quiet?: boolean; gps?: boolean }): void {
  // quiet = the tap opened a marker card on a phone: analyse, but don't expand the
  // sheet or re-centre the map over that card (popup-room.ts)
  const expand = (): void => { if (!opts?.quiet) ui.expand(); };
  // Ignore spurious / out-of-area points — e.g. a (0,0) "click" MapLibre can fire
  // during render — so the app never shows "ไม่พบลำน้ำ…" before a real tap (v0.9.1a §2).
  if (!(lon > 96 && lon < 106 && lat > 5 && lat < 21)) return;
  if (!isSimReady()) { renderMessage(ui.resultHost, "กำลังโหลดข้อมูลลำน้ำ… ลองอีกครั้งในอีกสักครู่"); ui.expand(); return; }
  const r = analyze(lon, lat, SUMMARY_RULES.riverMaxKm); // v0.9.5a D1: nearest main river ≤30 km
  if (!r) {
    renderMessage(ui.resultHost, "ไม่พบลำน้ำสายหลักใกล้จุดนี้ — ลองแตะบนเส้นแม่น้ำ หรือเพิ่มรัศมี");
    withMap((m) => clearRoute(m));
    ui.setLocateVisible(true);
    expand();
    return;
  }
  const centerLabel = opts?.centerLabel;
  exitOverview(false); // a new point leaves the country view (its layers come back for the new route)
  active = { lat, lon };
  updateCountryBtn();
  suspendProvinceRain(true); // the point's own rain chips / card take over (v0.9.6 A)
  reshow = () => analyzeAt(lon, lat, { ...opts, quiet: false });
  setPointIsHome(!!opts?.gps); // v0.9.9a: "บ้านคุณ" only for the user's own shared location, else "จุดนี้"
  ui.setLocateVisible(false);
  withMap((m) => { setRadius(m, { lon, lat }, radiusKm); setRadiusMask(m, { lon, lat }, radiusKm, maskSegments); setRoute(m, r.path, { lon, lat }, "unknown", opts?.gps ? "gps" : "picked"); setGistdaRadiusActive(m, true); void setGaugeFocus(m, { lat, lon }, radiusKm); });
  if (!opts?.quiet) fitRadius({ lat, lon }, radiusKm, 400);
  if (centerLabel) setStartLabel(centerLabel.replace(/^📍\s*/, ""));
  renderSummary(ui.resultHost, r, { lat, lon }, {
    radiusKm,
    onClear: () => { exitOverview(false); active = null; updateCountryBtn(); reshow = null; suspendProvinceRain(false); clearResult(ui.resultHost); clearSummary(); withMap((m) => { clearRoute(m); clearRadiusMask(m); setGistdaRadiusActive(m, false); void setGaugeFocus(m, null, 0); clearDamZones(m); }); ui.setLocateVisible(true); fitDefault(); },
    onRadius: (km) => { radiusKm = km; if (active) liveRadius(active, km); },
    onRadiusCommit: (km) => { radiusKm = km; debounceRecompute(); },
    onFlowStatus: (s: FlowStatus) => withMap((m) => setRoute(m, r.path, { lon, lat }, s, opts?.gps ? "gps" : "picked")),
    onPlace: (place) => { if (centerLabel) setStartLabel(centerLabel.replace(/^📍\s*/, "")); else if (place) setStartLabel(place.label); },
    onCameras: () => openCameras(),
    onDamZones: (data, i) => withMap((m) => { if (data) showDamZones(m, data, i, active ?? undefined); else clearDamZones(m); }),
  }, { centerLabel, back: opts?.back });
  expand();
}

/** Search result chosen → fit the area, set center at its centroid (a reference
 *  point, clearly not the user's house), keep the current radius. */
function goToSearch(e: SearchEntry): void {
  withMap((m) => m.fitBounds([[e.bbox[0], e.bbox[1]], [e.bbox[2], e.bbox[3]]], { padding: 48, maxZoom: 13, duration: 600 }));
  analyzeAt(e.lon, e.lat, { centerLabel: areaCenterLabel(e) });
}

function locate(): void {
  // item 2b: if a dam-rain day is being shown on the map, 💧 returns to the previous view first
  if (damZonesActive()) { withMap((m) => restoreDamView(m)); return; }
  if (!navigator.geolocation) { tapFallback("อุปกรณ์นี้ไม่รองรับ GPS"); return; }
  ui.setLocateBusy(true);
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      ui.setLocateBusy(false);
      const { longitude: lon, latitude: lat } = pos.coords;
      analyzeAt(lon, lat, { gps: true }); // analyzeAt fits the radius (with sheet padding) · pin says "คุณอยู่ที่นี่"
    },
    () => { ui.setLocateBusy(false); tapFallback("ไม่สามารถเข้าถึงตำแหน่งได้"); },
    { enableHighAccuracy: true, timeout: 10000 },
  );
}

function tapFallback(reason: string): void {
  renderMessage(ui.resultHost, `${reason} — <b>แตะบนแผนที่ตรงบ้านคุณ</b> เพื่อดูน้ำรอบ ๆ ได้เลย`);
  ui.expand();
}

// central–lower Thailand (sees เจ้าพระยา + อ่าวไทย) for the first screen
const CENTRAL_BBOX: [[number, number], [number, number]] = [[98.5, 12.5], [102.5, 16.5]];
/** Bottom padding so fitBounds keeps the content above the bottom sheet. */
function sheetPad(): { top: number; bottom: number; left: number; right: number } {
  const s = document.getElementById("sheet");
  const bottom = s && getComputedStyle(s).position === "fixed"
    ? Math.min(window.innerHeight * 0.62, s.getBoundingClientRect().height + 12) : 24;
  return { top: 86, bottom: Math.max(24, bottom), left: 24, right: 24 };
}
function fitDefault(): void { withMap((m) => m.fitBounds(CENTRAL_BBOX, { padding: sheetPad(), duration: 0 })); }

// live radius drag (item 8): mask reveal + counter + soft camera ease every frame;
// API/ETA/place recompute only on release (debounced).
let rafRadius = 0, recomputeTimer = 0, maskSegments = 64, lastFrameTs = 0, slowFrames = 0;
function liveRadius(center: { lat: number; lon: number }, km: number): void {
  cancelAnimationFrame(rafRadius);
  rafRadius = requestAnimationFrame((ts) => {
    if (lastFrameTs) { // drop circle resolution if we dip below ~30fps on the device
      const dt = ts - lastFrameTs;
      if (dt > 34) { if (++slowFrames >= 4 && maskSegments > 32) { maskSegments = 32; } } else slowFrames = 0;
    }
    lastFrameTs = ts;
    ui.setRadius(km); // keep the collapsed mini-bar slider in sync
    withMap((m) => { setRadius(m, center, km); setRadiusMask(m, center, km, maskSegments); void setGaugeFocus(m, center, km); });
    fitRadius(center, km, 140);
    const n = floodGauges(center, km).fresh.length;
    const live = document.getElementById("r-live");
    if (live) live.textContent = n > 0 ? ` · ในวง: จุดวัดน้ำท่วม ${n} จุด` : "";
  });
}
function debounceRecompute(): void {
  clearTimeout(recomputeTimer);
  recomputeTimer = window.setTimeout(() => { if (reshow) reshow(); }, 250);
}
/** Fit the radius circle, leaving room for the bottom sheet (item 2). */
function fitRadius(center: { lat: number; lon: number }, km: number, ms = 300): void {
  const dLat = km / 110.574, dLon = km / (111.32 * Math.cos((center.lat * Math.PI) / 180));
  withMap((m) => m.fitBounds([[center.lon - dLon, center.lat - dLat], [center.lon + dLon, center.lat + dLat]],
    { padding: sheetPad(), duration: ms, maxZoom: 15 }));
}

try {
  mapRef = createMap(mapEl);
  mountRiverChip(() => mapRef, sheetPad); // 〰 river name + distance under the search box (v0.9.5c)
  mountSosButton(); // 🆘 แจ้งเหตุ (no backend, v0.9.8)
  startPageTheme();
  topRight = mountTopRight(mapRef, { onCountry: () => toggleCountry(), onWeather: () => void toggleWeather(), onMonsoon: () => void toggleMonsoon(), onStorms: () => openWorldStorms(), onTheme: () => toggleTheme() });
  mapRef.on("moveend", updateCountryBtn);
  updateCountryBtn();
  (window as unknown as { __map?: unknown }).__map = mapRef; // read-only ref for diagnostics / e2e checks
  // e2e hook: render a storms payload (used to verify C3 rendering with REAL GDACS data,
  // since a cyclone is rarely near Thailand on any given day). Does not fetch or fake data.
  (window as unknown as { __renderStorms?: unknown }).__renderStorms = (d: unknown): void => { if (mapRef) renderStorms(mapRef, d as Parameters<typeof renderStorms>[1]); };
  mapRef.on("load", () => {
    try {
      addRiversLayer(mapRef!);
      addRiverLabelsLayer(mapRef!); // right above the river lines → below dams/staffs/route (v0.9.5c)
      addRouteLayer(mapRef!);
      addGistdaLayers(mapRef!);
      setGistdaWindow(mapRef!, DEFAULT_FLOOD_WINDOW);
      addNasaFloodLayers(mapRef!); // off by default — tiles fetch only when the user selects a window
      addSarFloodLayer(mapRef!); // CEMS GFM Sentinel-1 SAR — off by default; proxy serves nothing until configured
      void setDamsVisible(mapRef!, true).then(() => setDamsY54(mapRef!, true)); // "เทียบปี 54" on by default (owner, 21 ต.ค. 69) — after dams load so `dams` is populated
      void setBkkGaugesVisible(mapRef!, true); // POPNIX real gauges are the hero (v0.8.0)
      void initThaiWaterLayer(mapRef!); // over-bank stations visible by default (v0.9.1a)
      setRiverColorMode(mapRef!, "discharge");
      void loadRiverNames();
      // the historical-track simulation (nested inside the "พายุรอบโลก" panel + the ☰ menu) is offered
      // only once its track files are deployed (v0.9.6 C3 · v0.9.13: reveals the inner toggle, not a button)
      void fetch("/data/storms-past/index.json", { method: "HEAD" }).then((r) => { if (r.ok) { pastStormsAvailable = true; document.querySelectorAll<HTMLElement>(".past-storms-item").forEach((e) => (e.hidden = false)); window.dispatchEvent(new Event("fs-top-layout")); } }).catch(() => {});
      void installProvinceRain(mapRef!).then(() => suspendProvinceRain(active != null)); // v0.9.6 A: national 7-day rain
      mapRef!.getCanvas().style.cursor = "crosshair";
      // default view: central–lower Thailand (เจ้าพระยา + อ่าวไทย) unless a share link
      if (!parseShareUrl()) fitDefault();
    } catch (err) { console.error("failed to add layers", err); }
  });
  // Analyse on a genuine single tap only. A double-click (zoom) fires two `click`
  // events too — debounce so zooming never triggers "ไม่พบลำน้ำ…" (v0.9.1a §2).
  let clickTimer: ReturnType<typeof setTimeout> | undefined;
  mapRef.on("dblclick", () => clearTimeout(clickTimer));
  mapRef.on("click", (e) => {
    clearTimeout(clickTimer);
    const { lng, lat } = e.lngLat;
    // a day of the national rain map is on → a tap opens that province's rain card, not a point analysis
    if (provinceRainActive()) return;
    clickTimer = setTimeout(() => analyzeAt(lng, lat, { quiet: markerTapRecent() }), 160);
  });
  mapRef.on("error", (e) => console.error("map error", e?.error ?? e));
} catch (err) {
  console.error("map could not be initialised (WebGL?)", err);
  mapEl.classList.add("map-unavailable");
}

void (async () => {
  try {
    await loadSimData();
    const shared = parseShareUrl();
    if (shared) {
      if (shared.radiusKm) radiusKm = shared.radiusKm;
      const run = (): void => analyzeAt(shared.lon, shared.lat); // fits the radius itself
      // needs only our layers (added on "load"), not every tile: waiting for "idle" could take a
      // minute on a slow connection before the shared point showed up
      if (mapRef?.getLayer("route-highlight")) run();
      else mapRef?.once("load", () => setTimeout(run, 0)); // after the load handler that adds the layers
    }
  } catch (err) { console.error("failed to load sim data", err); }
  // Ambient flow (always-on dots + ripples) once the graph is ready — needs analyze().
  // window.__startup resolves once these start-up fetches are done (e2e gates count requests only after it)
  const begin = (): void => { if (mapRef) {
    const m = mapRef;
    installDamZoneLayers(m);
    // v0.9.12: fetch the live-storm data at start-up (feeds the summary row + the ▶ timeline) but
    // DON'T draw the map overlay here — the "ดูสภาพอากาศ" button draws it on demand so the first
    // screen never stalls on one big render ("แยกกดทีละเรื่อง ระบบจะได้ไม่ค้าง").
    (window as unknown as { __startup?: Promise<unknown> }).__startup = Promise.allSettled([startAmbient(m), loadStorms()]);
  } };
  // first "idle" (all tiles in) — or 4 s after "load" at the latest: a slow phone with software WebGL
  // may not reach "idle" for minutes, and these start-up layers only need our own data (v0.9.9a)
  let started = false;
  const go = (): void => { if (!started) { started = true; begin(); } };
  if (mapRef && mapRef.loaded()) go();
  else {
    mapRef?.once("idle", go);
    // "load" may already be past (the sim data above can take longer than the style)
    if (mapRef?.isStyleLoaded()) window.setTimeout(go, 4000); else mapRef?.once("load", () => window.setTimeout(go, 4000));
  }
})();

// Load ThaiWater station data in the background so the result card can show the
// nearest over/near station even before the layer is toggled on.
void loadThaiWaterData();

// Health chip: stay grey "กำลังตรวจสอบ…" while we wait (up to 8s, with one retry),
// show RED only when the backend actually reports a primary source down. A slow or
// unreachable probe is NOT a red alarm — it soft-degrades to amber instead, so the
// chip never cries "ข้อมูลไม่พร้อม" just because /api/health was slow to answer.
void (async () => {
  const getHealth = async (timeoutMs: number): Promise<"ok" | "degraded" | "down" | "disabled"> => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const r = await fetch("/api/health", { signal: ctrl.signal });
      const d = (await r.json()) as { status?: "ok" | "degraded" | "down" | "disabled" };
      return d.status ?? "degraded";
    } finally { clearTimeout(t); }
  };
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      ui.setStatus(await getHealth(8000)); // "down" here = backend confirms a critical source is down → red
      return;
    } catch {
      if (attempt === 0) { await new Promise((r) => setTimeout(r, 1200)); continue; } // keep grey, retry once
      ui.setStatus("degraded"); // couldn't reach health — warn amber, don't false-alarm red
    }
  }
})();
